import { PageLink } from '../../../../../_metronic/layout/core'
import { convertToUTCByMSK } from '../../../../../utils'
import { getDefaultDateRange } from '../../../../../utils/getDefaultDateRange'
import { ROLE } from '../../../auth'
import { PayoutRefusalListParams } from '../../../ticket-card/core/refusal.types'
import { PayoutRefusalFilterState } from './types'

export const PAGE_SIZE = 50

/**
 * Роли — как на бэкенде (PayoutRefusalController + security.yaml): только те, чью выборку сужает
 * скоуп мерчантов. Старший оператор и кассиры не допущены — им ушли бы отказы всех мерчантов.
 */
export const PAYOUT_REFUSAL_ROLES: ROLE[] = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_MATCHING_OPERATOR,
  ROLE.ROLE_MERCHANT_PAYMENTS_OPERATOR,
  ROLE.ROLE_MERCHANT_MANAGER,
]

export const PAYOUT_REFUSALS_TAB: PageLink = {
  title: 'Отказы',
  path: '/payout-tickets/refusals',
  isSeparator: false,
  isActive: false,
  roles: PAYOUT_REFUSAL_ROLES,
}

const STAFF_PAYOUT_ROLES: ROLE[] = [
  ROLE.ROLE_ADMIN,
  ROLE.ROLE_MANAGER,
  ROLE.ROLE_SENIOR_CASHIER,
  ROLE.ROLE_CASHIER,
  ROLE.ROLE_SENIOR_OPERATOR,
  ROLE.ROLE_OPERATOR,
]

/** Вкладки раздела выплат — те же ссылки, что у страниц «Активные», «Завершённые», «Отчёт». */
export const PAYOUT_REFUSALS_BREADCRUMBS: PageLink[] = [
  { title: 'Активные', path: '/payout-tickets/active', isSeparator: false, isActive: false, roles: STAFF_PAYOUT_ROLES },
  { title: 'Завершённые', path: '/payout-tickets/finished', isSeparator: false, isActive: false, roles: STAFF_PAYOUT_ROLES },
  {
    title: 'Отчёт',
    path: '/payout-tickets/report',
    isSeparator: false,
    isActive: false,
    roles: [ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER, ROLE.ROLE_SENIOR_CASHIER, ROLE.ROLE_SENIOR_OPERATOR],
  },
  { ...PAYOUT_REFUSALS_TAB, isActive: true },
]

export const payoutRefusalCardUrl = (id: number): string => `/payout-tickets/refusal/${id}`

type Option = { value: string; label: string }

/** Причины отказа для фильтра; подписи — те же, что бэкенд отдаёт в строках (PayoutRefusalVocabulary). */
export const PAYOUT_REFUSAL_REASON_OPTIONS: Option[] = [
  { value: 'PRECHECK_REJECTED', label: 'Мэтчинг не принял выплату' },
  { value: 'BANK_EXCLUDED', label: 'Банк получателя исключён из мэтчинга' },
  { value: 'AMOUNT_OUT_OF_RANGE', label: 'Сумма вне диапазонов включённых правил каскада' },
  { value: 'TRAFFIC_TYPE_MISMATCH', label: 'Тип трафика клиента не допущен правилами каскада' },
  { value: 'ALL_ADAPTERS_DISABLED', label: 'Все правила каскада выключены' },
  { value: 'NO_PRIORITY_CONFIGURED', label: 'Каскад для метода не настроен' },
  { value: 'BALANCE_INSUFFICIENT', label: 'Баланс мерчанта не покрывает выплату' },
]

/** Мультиселекты фильтров отказов: выбранное уходит из списка, чипы переносятся строками. */
export const MULTI_SELECT_PROPS = { placeholder: 'Все', wrapChips: true, hideSelectedOptions: true }

/** Открывается на текущих сутках по МСК, сброс возвращает туда же — как остальные списки панели. */
export const defaultRefusalFilters = (): Pick<PayoutRefusalFilterState, 'dateRange'> => {
  const { startDate, endDate } = getDefaultDateRange()

  return { dateRange: { from: startDate, to: endDate } }
}

export const amount = (value?: string): number | undefined => {
  const normalized = value?.trim().replace(',', '.')
  if (!normalized) return undefined
  const n = Number(normalized)
  return Number.isFinite(n) ? n : undefined
}

export const nonEmpty = <T,>(list?: T[]): T[] | undefined => (list && list.length ? list : undefined)

export const toPayoutRefusalParams = (f: PayoutRefusalFilterState, page: number): PayoutRefusalListParams => ({
  createdAtStart: f.dateRange?.from ? convertToUTCByMSK(f.dateRange.from) : undefined,
  createdAtEnd: f.dateRange?.to ? convertToUTCByMSK(f.dateRange.to) : undefined,
  merchantTokens: nonEmpty(f.merchantTokens),
  paymentMethodTypes: nonEmpty(f.paymentMethodTypes),
  reasons: nonEmpty(f.reasons),
  currency: f.currency?.trim() || undefined,
  amountFrom: amount(f.amountRange?.from),
  amountTo: amount(f.amountRange?.to),
  transactionId: f.transactionId?.trim() || undefined,
  page,
  perPage: PAGE_SIZE,
})
